/**
 * Maps pipeline v0.1 orchestrator `process_turn` JSON (compatibility / "old" format)
 * into TurnAnalysisTurn so LiveAssistCard can render without a transcript/ta row.
 */
import type {
  TurnAnalysisTurn,
  StateActionOrderedItem,
  StateActionViolationItem,
  StateEntityFullItem,
} from '../types/api';
import { serializeEntityValue } from './entityValue';

function normalizeGuidance(raw: unknown): string[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map((x) => (typeof x === 'string' ? x : JSON.stringify(x)));
  }
  if (typeof raw === 'string') return [raw];
  return [JSON.stringify(raw)];
}

function normalizeViolations(raw: unknown): (StateActionViolationItem | string)[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((v) => {
    if (v != null && typeof v === 'object' && !Array.isArray(v)) {
      const o = v as Record<string, unknown>;
      return {
        icon: o.icon as string | undefined,
        violation_type: (o.violation_type ?? o.violationType) as string | undefined,
        message: o.message as string | undefined,
        severity: o.severity as string | undefined,
      };
    }
    return String(v);
  });
}

/**
 * Map engine ``StepInfo.status`` (same strings persisted to ta_turn_action.status) to
 * ``StateActionOrderedItem.status`` for the visualiser. Must not collapse
 * ``awaiting_customer_response`` / ``awaiting_confirmation`` to ``pending`` — timeline
 * treats ``pending`` in past sections as missed and AgentAssistLiveCard only heroes
 * ``awaiting_customer_response``.
 */
function mapEngineRowStatus(row: Record<string, unknown>): string {
  const s = String(row.status ?? 'not_started').toLowerCase();
  if (s === 'completed') return 'completed';
  if (s === 'failed') return 'failed';
  if (s === 'missed' || s === 'missed_mandatory' || s === 'missed_inorder') return 'missed';
  if (s === 'skipped') return 'skipped';
  if (s === 'not_started') return 'not_started';
  if (
    s === 'awaiting_customer_response' ||
    s === 'awaiting_confirmation' ||
    s === 'handle_customer_response'
  ) {
    return s;
  }
  if (s === 'in_progress' || s === 'started') return 'live';
  return 'pending';
}

function buildStateActionsOrdered(result: Record<string, unknown>): StateActionOrderedItem[] {
  const rows = result.context_actions_for_log;
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return (rows as Record<string, unknown>[]).map((row, idx) => {
    const name = String(row.name ?? '');
    const st = mapEngineRowStatus(row);
    const mandatory = Boolean(row.mandatory);
    const inorder = Boolean(row.inorder);
    const constraints: string[] = [];
    if (mandatory) constraints.push('mandatory');
    if (inorder) constraints.push('inorder');
    const wfState = row.wf_state != null ? String(row.wf_state).trim() : '';
    const subName = row.subflow_name != null ? String(row.subflow_name).trim() : '';
    const sid = row.step_instance_id;
    const attempts = typeof row.attempts === 'number' ? row.attempts : undefined;
    const stageRaw = row.stage;
    const expected_action_stage =
      typeof stageRaw === 'string' && stageRaw.trim() ? stageRaw.trim() : undefined;
    const item: StateActionOrderedItem = {
      step_id: name,
      canonical_action_name: name,
      // Canonical id for matching (same as TA export); display uses step_title.
      step_text: name,
      status: st,
      order_in_state: typeof row.order_in_state === 'number' ? row.order_in_state : idx,
      constraints: constraints.length > 0 ? constraints : undefined,
      violations: normalizeViolations(row.violations),
      primary_entity_name: (row.primary_entity_name as string | null) ?? null,
      step_title: (row.step_title as string | null) ?? null,
      step_subtitle: (row.step_subtitle as string | null) ?? null,
      assist_text: (row.assist_text as string | null) ?? null,
      heads_up: Array.isArray(row.heads_up) ? (row.heads_up as StateActionOrderedItem['heads_up']) : undefined,
      action_type: st === 'missed' && inorder ? 'inorder' : undefined,
      subflow_name: subName.length > 0 ? subName : null,
      wf_state_name: wfState.length > 0 ? wfState : null,
      step_instance_id: typeof sid === 'string' || typeof sid === 'number' ? String(sid) : null,
      attempts,
      expected_action_stage,
    };
    return item;
  });
}

function buildStateEntitiesFull(result: Record<string, unknown>): StateEntityFullItem[] {
  const all = result.all_entities;
  if (Array.isArray(all) && all.length > 0) {
    return (all as Record<string, unknown>[]).map((row) => {
      const name = String(row.name ?? '');
      const serialized = serializeEntityValue(row.value);
      const has = serialized != null;
      return {
        entity_name: name,
        value: serialized,
        filled: has,
        status: has ? 'completed' : 'not_filled',
        stage: null,
      };
    });
  }
  const entMap = result.entities as Record<string, { value?: unknown; stage?: string; collected?: boolean }> | undefined;
  if (!entMap || typeof entMap !== 'object') return [];
  return Object.entries(entMap).map(([entityName, v]) => {
    const serialized = serializeEntityValue(v.value);
    const has = serialized != null;
    return {
      entity_name: entityName,
      value: serialized,
      filled: Boolean(v.collected) || has,
      status: has ? 'completed' : 'not_filled',
      stage: v.stage ?? null,
    };
  });
}

function normalizeStartState(raw: unknown): string | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    return t.length > 0 ? t : null;
  }
  if (typeof raw === 'object' && raw !== null && 'state' in (raw as Record<string, unknown>)) {
    const s = (raw as { state?: unknown }).state;
    return typeof s === 'string' && s.trim() ? s.trim() : null;
  }
  return null;
}

export function orchestratorResultToTurn(
  turnNumber: number,
  speaker: string,
  utterance: string,
  result: Record<string, unknown>
): TurnAnalysisTurn {
  const stateTitle = String(result.state ?? '');
  const guidance = normalizeGuidance(result.guidance);
  const stateActions = buildStateActionsOrdered(result);
  const stateEntitiesFull = buildStateEntitiesFull(result);
  const start_state = normalizeStartState(result.state_at_turn_start);
  const taMeta = (result.ta_turn_meta ?? {}) as Record<string, unknown>;
  const focusStepIdRaw = taMeta.focus_step_id;
  const focus_step_id =
    typeof focusStepIdRaw === 'string' && focusStepIdRaw.trim()
      ? focusStepIdRaw.trim()
      : focusStepIdRaw != null
        ? String(focusStepIdRaw)
        : null;

  return {
    speech_text_id: turnNumber,
    turn_number: turnNumber,
    timestamp: new Date().toISOString(),
    speaker,
    text: utterance,
    actions: [],
    strict_state: stateTitle ? { state: stateTitle } : null,
    start_state,
    focus_step_id,
    free_flow_state: null,
    signals: [],
    warnings: Array.isArray(result.warnings)
      ? (result.warnings as unknown[]).map((w) => String(w))
      : [],
    guidance,
    next_actions: { strict: [], free_flow: [] },
    agent_card: {
      strict_state_title: stateTitle,
      current_state_title: stateTitle,
      completed_steps: [],
      missed_steps: [],
      ongoing_steps: [],
      upcoming_steps: [],
      collected_entities: [],
      pending_entities: [],
      attention: [],
      signals: [],
      state_actions_ordered: stateActions.length > 0 ? stateActions : undefined,
      state_entities_full: stateEntitiesFull.length > 0 ? stateEntitiesFull : undefined,
    },
  };
}
